import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Space, Button, message, Select, Typography } from 'antd';
import { UserOutlined, SettingOutlined, DeleteOutlined } from '@ant-design/icons';
import { fetchUsers, deleteUser, updateUserRole } from '../../actions/users';
import { ROLES } from '../../types/permissions';
import { useDispatch, useSelector } from 'react-redux';

const { Option } = Select;
const { Text } = Typography;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const currentUser = useSelector(state => state.auth.user);
  const dispatch = useDispatch();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await dispatch(fetchUsers());
      setUsers(data);
    } catch (error) {
      message.error('Failed to load users');
    }
    setLoading(false);
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      message.error('Cannot delete your own account');
      return;
    }

    try {
      await deleteUser(userId);
      message.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      message.error('Failed to delete user');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentUser?.id) {
      message.error('Cannot change your own role');
      return;
    }

    try {
      await updateUserRole(userId, newRole);
      message.success('Role updated successfully');
      loadUsers();
    } catch (error) {
      message.error('Failed to update role');
    }
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'email',
      key: 'email',
      render: (email, record) => (
        <Space>
          <UserOutlined />
          <div>
            <Text strong>{record.name || email}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>{email}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 200,
      render: (role, record) => (
        <Select
          value={role}
          style={{ width: 120 }}
          onChange={(newRole) => handleRoleChange(record.id, newRole)}
          disabled={!currentUser?.isAdmin || record.id === currentUser?.id}
        >
          {Object.values(ROLES).map(r => (
            <Option key={r} value={r}>
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<SettingOutlined />}
            onClick={() => navigate(`/users/${record.userId}/permissions`)}
          >
            Permissions
          </Button>
          {currentUser?.isAdmin && record.id !== currentUser?.id && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteUser(record.id)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">User Management</h1>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} users`
        }}
      />
    </div>
  );
};

export default UserList;
